import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, X, ChevronRight, ChevronLeft, Sparkles, Check, Image as ImageIcon, User, Palette, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { categoryAPI, backgroundAPI, modelAPI, styleAPI, generateAPI, uploadAPI, getImageUrl } from "../lib/api";
import UserLayout from "../components/UserLayout";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const GeneratePage = () => {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const { t } = useLanguage();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    
    // Form Data
    const [photoType, setPhotoType] = useState("single_product");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [selectedBackground, setSelectedBackground] = useState(null);
    const [useModel, setUseModel] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedStyle, setSelectedStyle] = useState(null);
    const [tokensNeeded, setTokensNeeded] = useState(0);

    // Options Data
    const [categories, setCategories] = useState([]);
    const [backgrounds, setBackgrounds] = useState([]);
    const [models, setModels] = useState([]);
    const [styles, setStyles] = useState([]);

    useEffect(() => {
        fetchCategories();
        fetchModels();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchBackgrounds(selectedCategory);
            fetchStyles(selectedCategory);
        }
    }, [selectedCategory]);

    useEffect(() => {
        calculateTokens();
    }, [uploadedUrls, selectedBackground, selectedModel, selectedStyle]);

    const fetchCategories = async () => {
        try {
            const response = await categoryAPI.getActive();
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchBackgrounds = async (categoryId) => {
        try {
            const response = await backgroundAPI.getAll(categoryId);
            setBackgrounds(response.data);
        } catch (error) {
            console.error("Error fetching backgrounds:", error);
        }
    };

    const fetchModels = async () => {
        try {
            const response = await modelAPI.getActive();
            setModels(response.data);
        } catch (error) {
            console.error("Error fetching models:", error);
        }
    };

    const fetchStyles = async (categoryId) => {
        try {
            const response = await styleAPI.getAll(categoryId);
            setStyles(response.data);
        } catch (error) {
            console.error("Error fetching styles:", error);
        }
    };

    const calculateTokens = async () => {
        if (uploadedUrls.length > 0 && selectedBackground) {
            try {
                const response = await generateAPI.calculateTokens({
                    photo_type: photoType,
                    category_id: selectedCategory,
                    product_image_urls: uploadedUrls,
                    background_id: selectedBackground,
                    model_artist_id: useModel ? selectedModel : null,
                    style_id: useModel ? selectedStyle : null
                });
                setTokensNeeded(response.data.tokens_needed);
            } catch (error) {
                console.error("Error calculating tokens:", error);
            }
        }
    };

    const handleFileUpload = async (files) => {
        setLoading(true);
        const newUrls = [];

        try {
            for (const file of files) {
                const response = await uploadAPI.upload(file);
                newUrls.push(response.data.url);
            }
            setUploadedFiles([...uploadedFiles, ...Array.from(files)]);
            setUploadedUrls([...uploadedUrls, ...newUrls]);
            toast.success(`${files.length} file berhasil diupload`);
        } catch (error) {
            toast.error("Gagal upload file");
        } finally {
            setLoading(false);
        }
    };

    const removeFile = (index) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
        setUploadedUrls(uploadedUrls.filter((_, i) => i !== index));
    };

    const handleGenerate = async () => {
        if (user.credits < tokensNeeded) {
            toast.error("Credits tidak cukup. Silakan beli credits terlebih dahulu.");
            return;
        }

        setGenerating(true);
        try {
            const response = await generateAPI.generate({
                photo_type: photoType,
                category_id: selectedCategory,
                product_image_urls: uploadedUrls,
                background_id: selectedBackground,
                model_artist_id: useModel ? selectedModel : null,
                style_id: useModel ? selectedStyle : null
            });

            toast.success("Gambar berhasil digenerate!");
            await refreshUser();
            navigate("/gallery");
        } catch (error) {
            toast.error(error.response?.data?.detail?.error || "Gagal generate gambar");
        } finally {
            setGenerating(false);
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return photoType;
            case 2: return selectedCategory;
            case 3: return uploadedUrls.length > 0;
            case 4: return selectedBackground;
            case 5: return !useModel || (selectedModel && selectedStyle);
            default: return true;
        }
    };

    const steps = [
        { number: 1, label: t('select_photo_type') },
        { number: 2, label: t('category') },
        { number: 3, label: t('upload_product') },
        { number: 4, label: t('select_background') },
        { number: 5, label: t('model_style') }
    ];

    return (
        <UserLayout>
            <div className="max-w-5xl mx-auto" data-testid="generate-page">
                {/* Steps Progress */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        {steps.map((s, index) => (
                            <div key={s.number} className="flex items-center">
                                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-medium transition-all ${
                                    step > s.number 
                                        ? 'bg-green-500 text-white' 
                                        : step === s.number 
                                            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
                                            : 'bg-gray-100 text-gray-400'
                                }`}>
                                    {step > s.number ? <Check className="w-5 h-5" /> : s.number}
                                </div>
                                <span className={`ml-2 hidden sm:block ${step >= s.number ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {s.label}
                                </span>
                                {index < steps.length - 1 && (
                                    <div className={`w-8 md:w-16 h-0.5 mx-2 ${step > s.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step Content */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 min-h-[400px]">
                    {/* Step 1: Photo Type */}
                    {step === 1 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('select_photo_type')}</h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                <button
                                    onClick={() => { setPhotoType("single_product"); setUseModel(false); }}
                                    className={`p-6 rounded-2xl border-2 text-left transition-all ${
                                        photoType === "single_product" 
                                            ? 'border-red-500 bg-red-50' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    data-testid="photo-type-single"
                                >
                                    <ImageIcon className={`w-10 h-10 mb-4 ${photoType === "single_product" ? 'text-red-500' : 'text-gray-400'}`} />
                                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{t('single_product')}</h3>
                                    <p className="text-gray-600">{t('single_product_desc')}</p>
                                </button>
                                <button
                                    onClick={() => { setPhotoType("with_model"); setUseModel(true); }}
                                    className={`p-6 rounded-2xl border-2 text-left transition-all ${
                                        photoType === "with_model" 
                                            ? 'border-red-500 bg-red-50' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    data-testid="photo-type-model"
                                >
                                    <User className={`w-10 h-10 mb-4 ${photoType === "with_model" ? 'text-red-500' : 'text-gray-400'}`} />
                                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{t('with_model')}</h3>
                                    <p className="text-gray-600">{t('with_model_desc')}</p>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Category */}
                    {step === 2 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('select_category')}</h2>
                            {categories.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setSelectedCategory(cat.id)}
                                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                                                selectedCategory === cat.id 
                                                    ? 'border-red-500 bg-red-50' 
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                            data-testid={`category-${cat.id}`}
                                        >
                                            <span className="font-medium text-gray-900">{cat.name}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    {t('no_categories')}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Step 3: Upload */}
                    {step === 3 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('upload_product')}</h2>
                            <div
                                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                                    loading ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                }`}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    handleFileUpload(e.dataTransfer.files);
                                }}
                            >
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => handleFileUpload(e.target.files)}
                                    className="hidden"
                                    id="file-upload"
                                    disabled={loading}
                                    data-testid="file-upload-input"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    {loading ? (
                                        <Loader2 className="w-12 h-12 text-red-500 mx-auto mb-4 animate-spin" />
                                    ) : (
                                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    )}
                                    <p className="text-gray-600 mb-2">
                                        {loading ? t('loading') : t('drag_drop')}
                                    </p>
                                    <p className="text-sm text-gray-400">{t('file_limit')}</p>
                                </label>
                            </div>

                            {uploadedFiles.length > 0 && (
                                <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-4">
                                    {uploadedFiles.map((file, index) => (
                                        <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                onClick={() => removeFile(index)}
                                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                                data-testid={`remove-file-${index}`}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Step 4: Background */}
                    {step === 4 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('select_background')}</h2>
                            {backgrounds.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {backgrounds.map((bg) => (
                                        <button
                                            key={bg.id}
                                            onClick={() => setSelectedBackground(bg.id)}
                                            className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                                                selectedBackground === bg.id 
                                                    ? 'border-red-500 ring-2 ring-red-500/20' 
                                                    : 'border-transparent hover:border-gray-300'
                                            }`}
                                            data-testid={`background-${bg.id}`}
                                        >
                                            <img
                                                src={getImageUrl(bg.thumbnail_url)}
                                                alt={bg.name}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                            <span className="absolute bottom-2 left-2 text-white text-sm font-medium">{bg.name}</span>
                                            {selectedBackground === bg.id && (
                                                <div className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                                                    <Check className="w-4 h-4 text-white" />
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    {t('no_backgrounds')}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Step 5: Model & Style */}
                    {step === 5 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('model_style')}</h2>
                            
                            {photoType === "single_product" ? (
                                <div className="text-center py-12">
                                    <Palette className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 mb-4">{t('no_model_mode')}</p>
                                    <p className="text-sm text-gray-400">{t('continue_next')}</p>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* Model Selection */}
                                    <div>
                                        <Label className="text-lg font-semibold mb-4 block">{t('select_model')}</Label>
                                        {models.length > 0 ? (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {models.map((model) => (
                                                    <button
                                                        key={model.id}
                                                        onClick={() => setSelectedModel(model.id)}
                                                        className={`relative aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all ${
                                                            selectedModel === model.id 
                                                                ? 'border-red-500 ring-2 ring-red-500/20' 
                                                                : 'border-transparent hover:border-gray-300'
                                                        }`}
                                                        data-testid={`model-${model.id}`}
                                                    >
                                                        <img
                                                            src={getImageUrl(model.thumbnail_url)}
                                                            alt={model.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                                        <span className="absolute bottom-2 left-2 text-white text-sm font-medium">{model.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500">{t('no_models')}</p>
                                        )}
                                    </div>

                                    {/* Style Selection */}
                                    <div>
                                        <Label className="text-lg font-semibold mb-4 block">{t('select_style')}</Label>
                                        {styles.length > 0 ? (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {styles.map((style) => (
                                                    <button
                                                        key={style.id}
                                                        onClick={() => setSelectedStyle(style.id)}
                                                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                                                            selectedStyle === style.id 
                                                                ? 'border-red-500 ring-2 ring-red-500/20' 
                                                                : 'border-transparent hover:border-gray-300'
                                                        }`}
                                                        data-testid={`style-${style.id}`}
                                                    >
                                                        <img
                                                            src={getImageUrl(style.thumbnail_url)}
                                                            alt={style.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                                        <span className="absolute bottom-2 left-2 text-white text-sm font-medium">{style.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500">{t('no_styles')}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="mt-6 flex items-center justify-between">
                    <Button
                        variant="outline"
                        onClick={() => setStep(step - 1)}
                        disabled={step === 1}
                        className="rounded-xl"
                        data-testid="prev-step-btn"
                    >
                        <ChevronLeft className="w-5 h-5 mr-1" />
                        {t('back')}
                    </Button>

                    <div className="flex items-center gap-4">
                        {tokensNeeded > 0 && (
                            <div className="text-right">
                                <p className="text-sm text-gray-500">{t('tokens_needed')}</p>
                                <p className="font-mono font-bold text-lg text-gray-900">{tokensNeeded}</p>
                            </div>
                        )}

                        {step < 5 ? (
                            <Button
                                onClick={() => setStep(step + 1)}
                                disabled={!canProceed()}
                                className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                                data-testid="next-step-btn"
                            >
                                {t('next')}
                                <ChevronRight className="w-5 h-5 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleGenerate}
                                disabled={generating || !canProceed() || user?.credits < tokensNeeded}
                                className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                                data-testid="generate-btn"
                            >
                                {generating ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        {t('generating')}
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5 mr-2" />
                                        {t('generate')} ({tokensNeeded} {t('credits')})
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </UserLayout>
    );
};

export default GeneratePage;
